import React, { useEffect } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import { Button, DatePicker, Form, Input, Select, Space } from "antd";

import { Page } from "../../components/page";
import {
  VIEW_AIRLINES_PAGE_PERM,
  VIEW_AUCTION_PAGE_PERM,
  VIEW_BOOKINGS_PAGE_PERM,
  VIEW_CLICKS_PAGE_PERM,
  VIEW_CONVERSIONS_PAGE_PERM,
  VIEW_DIRECTIONS_PAGE_PERM,
  VIEW_ERRORS_PAGE_PERM,
  VIEW_HOSTS_PAGE_PERM,
} from "../../permissions";
import { sendRequest } from "../../store/extended_report/actions";
import { ExtendedReportPayload } from "../../store/extended_report/types";
import { RootState } from "../../store/root_reducer";

const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

export const ExtendedReportForm: React.FC = () => {
  const { t } = useTranslation(["extended_report_page"]);

  const [form] = Form.useForm();

  const selectedGate = useSelector((state: RootState) => state.Gates.selectedGate);
  const user = useSelector((state: RootState) => state.User);

  const [reportParam, setReportParam] = useQueryParam("report", withDefault(StringParam, ""));

  const dispatch = useDispatch();

  const onFinish = (message: ExtendedReportPayload) => {
    dispatch(
      sendRequest({
        ...message,
        gateId: selectedGate,
      })
    );
    form.resetFields();
  };

  useEffect(() => {
    setReportParam((state) => state, "replaceIn");
  }, [reportParam, setReportParam]);

  const reports = [
    {
      name: t("form.reports.conversions"),
      value: "conversions",
      permission: VIEW_CONVERSIONS_PAGE_PERM,
    },
    {
      name: t("form.reports.hosts"),
      value: "hosts",
      permission: VIEW_HOSTS_PAGE_PERM,
    },
    {
      name: t("form.reports.clicks"),
      value: "clicks",
      permission: VIEW_CLICKS_PAGE_PERM,
    },
    {
      name: t("form.reports.airlines"),
      value: "airlines",
      permission: VIEW_AIRLINES_PAGE_PERM,
    },
    {
      name: t("form.reports.bookings"),
      value: "bookings",
      permission: VIEW_BOOKINGS_PAGE_PERM,
    },
    {
      name: t("form.reports.directions"),
      value: "directions",
      permission: VIEW_DIRECTIONS_PAGE_PERM,
    },
    {
      name: t("form.reports.auction"),
      value: "dynamic_auction",
      permission: VIEW_AUCTION_PAGE_PERM,
    },
    {
      name: t("form.reports.errors"),
      value: "errors",
      permission: VIEW_ERRORS_PAGE_PERM,
    },
  ];

  return (
    <Page title={t("title")} description={t("description")}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Form
          name="basic"
          initialValues={{ remember: true }}
          onFinish={onFinish}
          form={form}
          style={{ width: "30%" }}
          layout="vertical"
        >
          <Form.Item
            name="report"
            label={t("form.reportLabel")}
            initialValue={reportParam}
            rules={[{ required: true, message: t("form.messageValidationError") }]}
          >
            <Select showSearch>
              {reports.map((i) => user.permissions.includes(i.permission) && <Option value={i.value}>{i.name}</Option>)}
            </Select>
          </Form.Item>

          <Form.Item name="email" label={t("form.emailLabel")} rules={[{ required: false, type: "email" }]}>
            <Input />
          </Form.Item>

          <Form.Item
            name="range"
            label={t("form.dateRangeLabel")}
            rules={[{ required: true, message: t("form.messageValidationError") }]}
          >
            <RangePicker format="YYYY-MM-DD" />
          </Form.Item>

          <Form.Item name="comment" label={t("form.commentLabel")}>
            <TextArea />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              {t("form.sendButton")}
            </Button>
          </Form.Item>
        </Form>
      </Space>
    </Page>
  );
};
